import { useState, useCallback, useEffect } from "react";
import { ITransaction } from "src/domains";
import { AppState, AppStateStatus } from "react-native";
import {
  queryGetTransactions,
} from "src/graphql";
import {
  transactionBuyEmit,
  TransactionBuyEvent,
} from "src/utils/event";
import { EventRegister } from "react-native-event-listeners";
import axios from "axios";
import { paymentEndpoint } from "src/aws-config";
import { useStripe } from "@stripe/react-stripe-js";

type ActionRequiredResponse = {
  error: "action_required";
  message: string;
  client_secret: string;
  payment_intent_id?: string;
};

export const useTransactions = () => {
  const [items, setItems] = useState<ITransaction[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | undefined>();
  let _isMount = true;

  const refresh = useCallback(async () => {
    if (!_isMount) {
      return;
    }
    if (_isMount) {
      setLoading(true);
    }
    try {
      const response = await queryGetTransactions();
      if (_isMount) {
        setItems(response.data?.getTransactions?.items || []);
      }
    } catch (e) {
      console.error(e);
      if (_isMount) {
        setError(e as Error);
      }
    }
    if (_isMount) {
      setLoading(false);
    }
  }, []);

  const onAppStateChange = useCallback(
    (state: AppStateStatus) => {
      if (state === "active") {
        refresh();
      }
    },
    [refresh]
  );

  useEffect(() => {
    AppState.addEventListener("change", onAppStateChange);
    // TODO: Check if it works corrent (muliti hundler)
    // return () => {
    //   AppState.removeEventListener('change', onAppStateChange);
    // };
  }, [onAppStateChange]);

  useEffect(() => {
    const transactionBuyListener = EventRegister.addEventListener(
      TransactionBuyEvent,
      () => refresh()
    );
    return () => {
      EventRegister.removeEventListener(transactionBuyListener as string);
    };
  }, [refresh]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    _isMount = true;
    return () => {
      _isMount = false;
    };
  }, []);

  return [items, loading, error, refresh] as const;
};

export const useTransactionBuy = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | undefined>();
  const stripe = useStripe();

  const handler = useCallback(async (username: string, code: string) => {
    setLoading(true);
    setError(undefined);

    try {
      // 1) サーバにオフセッション請求を依頼
      const res = await axios.post(
        `${paymentEndpoint}/${username}/process-payment`,
        { amount: Number(code) },
        {
          headers: { "Idempotency-Key": `${username}:${code}` },
          validateStatus: () => true, // 402 を catch ではなく分岐で扱う
        }
      );

      // 2) フリクションレス成功
      if (res.status === 200) {
        transactionBuyEmit();
        setLoading(false);
        return true;
      }

      // 3) 追加認証が必要（402 + client_secret）
      if (res.status === 402 && res.data?.error === "action_required") {
        if (!stripe) {
          setError(new Error("Stripe 未初期化です。ページを再読み込みしてください。"));
          setLoading(false);
          return false;
        }

        const data = res.data as ActionRequiredResponse;
        const result = await stripe.confirmPayment({
          clientSecret: data.client_secret,
          // Webでは return_url を指定してもOK。SPAなら不要でも可。
          // confirmParams: { return_url: window.location.href },
          redirect: "if_required", // 3DSが必要なときだけ画面遷移/モーダル
        });

        if (result.error) {
          setError(new Error(result.error.message || "認証に失敗しました"));
          setLoading(false);
          return false;
        }

        const status = result.paymentIntent?.status?.toLowerCase();
        if (status === "succeeded" || status === "requires_capture") {
          transactionBuyEmit();
          setLoading(false);
          return true;
        }

        setError(new Error(`未確定ステータス: ${result.paymentIntent?.status}`));
        setLoading(false);
        return false;
      }

      // 4) カード拒否などの業務エラー
      if (res.status === 402) {
        setError(new Error(res.data?.message || "カードエラーが発生しました"));
        setLoading(false);
        return false;
      }

      // 5) 想定外（500など）
      setError(new Error(res.data?.message || "Internal Server Error"));
      setLoading(false);
      return false;

    } catch (e) {
      console.error(e);
      setError(e as Error);
      setLoading(false);
      return false;
    }
  }, [stripe]);

  return [handler, loading, error] as const;
};
